/**
 * Member portal trackers — journeys, submissions, and progress (demo storage).
 * Logged-in members see trackers they belong to from here.
 */

const TRACKERS_KEY = "gymrite_member_trackers_v1";
const SUBMISSIONS_KEY = "gymrite_member_submissions_v1";
const DEMO_FLAG_KEY = "gymrite_member_trackers_demo_v3";

export const TRACKER_KINDS = {
  STANDARD: "standard",
  JOURNEY: "journey",
};

/** List buckets on member portal */
export const TRACKER_LIST_SECTIONS = {
  MY: "my",
  NEW: "new",
};

export const PREVIEW_LIMIT = 3;

const PRIMARY = "var(--portal-theme-primary, #04abbb)";

const nowIso = () => new Date().toISOString();

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString();
};

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const buildMemberDemoTrackers = () => [
  // —— My trackers (enrolled / in progress / completed) ——
  {
    id: "tracker_chasing_better",
    name: "Chasing Better Journey",
    description:
      "Your 12-week transformation — baseline, then 30 / 60 / 90 day reviews.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "chasing-better-journey",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "active",
    coachName: "Sarah Murphy",
    statusName: "In Progress",
    statusColor: "#047857",
    startedAt: daysAgo(45),
    nextReviewAt: daysFromNow(15),
    progressPercent: 50,
    forms: [
      {
        id: "form_cb_signup",
        name: "Sign-up & Baseline",
        role: "first_submission",
        dueLabel: "Day 0",
        unlockDays: 0,
      },
      {
        id: "form_cb_30",
        name: "30 Day Review",
        role: "follow_up",
        dueLabel: "Day 30",
        unlockDays: 30,
      },
      {
        id: "form_cb_60",
        name: "60 Day Review",
        role: "follow_up",
        dueLabel: "Day 60",
        unlockDays: 60,
      },
      {
        id: "form_cb_90",
        name: "90 Day Review",
        role: "final",
        dueLabel: "Day 90",
        unlockDays: 90,
      },
    ],
  },
  {
    id: "tracker_quarterly_review",
    name: "Quarterly Member Review",
    description: "Seasonal check-in with your coach on goals and progress.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "quarterly-member-review",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "active",
    coachName: "James O'Brien",
    statusName: "Review Due",
    statusColor: "#7c3aed",
    startedAt: daysAgo(80),
    nextReviewAt: daysAgo(2),
    progressPercent: 33,
    forms: [
      {
        id: "form_qr_q1",
        name: "Q1 Review",
        role: "first_submission",
        dueLabel: "Q1",
        unlockDays: 0,
      },
      {
        id: "form_qr_q2",
        name: "Q2 Review",
        role: "follow_up",
        dueLabel: "Q2",
        unlockDays: 90,
      },
      {
        id: "form_qr_q3",
        name: "Q3 Review",
        role: "follow_up",
        dueLabel: "Q3",
        unlockDays: 180,
      },
    ],
  },
  {
    id: "tracker_coach_checkin",
    name: "Weekly Coach Check-in",
    description: "Quick weekly pulse — energy, training, and blockers.",
    kind: TRACKER_KINDS.STANDARD,
    category: "journey",
    publicSlug: "coach-check-in",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "active",
    coachName: "Aoife Walsh",
    statusName: "Active",
    statusColor: "#0369a1",
    startedAt: daysAgo(21),
    nextReviewAt: daysFromNow(3),
    progressPercent: 70,
    forms: [
      {
        id: "form_ci_main",
        name: "Check-in form",
        role: "first_submission",
        dueLabel: "Weekly",
        unlockDays: 0,
      },
    ],
  },
  {
    id: "tracker_summer_done",
    name: "Summer Strength Block",
    description: "8-week strength block you completed earlier this year.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "summer-strength-block",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "completed",
    coachName: "Sarah Murphy",
    statusName: "Completed",
    statusColor: "#0369a1",
    startedAt: daysAgo(120),
    nextReviewAt: null,
    progressPercent: 100,
    forms: [
      {
        id: "form_ss_start",
        name: "Baseline",
        role: "first_submission",
        dueLabel: "Start",
        unlockDays: 0,
      },
      {
        id: "form_ss_mid",
        name: "Mid-block review",
        role: "follow_up",
        dueLabel: "Week 4",
        unlockDays: 28,
      },
      {
        id: "form_ss_end",
        name: "Final review",
        role: "final",
        dueLabel: "Week 8",
        unlockDays: 56,
      },
    ],
  },
  {
    id: "tracker_nutrition_pulse",
    name: "Nutrition Pulse",
    description: "Short nutrition check-ins while you stay on plan.",
    kind: TRACKER_KINDS.STANDARD,
    category: "journey",
    publicSlug: "nutrition-pulse",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "active",
    coachName: "Aoife Walsh",
    statusName: "In Progress",
    statusColor: "#047857",
    startedAt: daysAgo(12),
    nextReviewAt: daysFromNow(2),
    progressPercent: 40,
    forms: [
      {
        id: "form_np_main",
        name: "Nutrition check-in",
        role: "first_submission",
        dueLabel: "Ongoing",
        unlockDays: 0,
      },
    ],
  },
  // —— New to you (available, not started) ——
  {
    id: "tracker_summer_challenge",
    name: "Summer Challenge 2026",
    description: "Join the gym-wide summer challenge — leaderboard and weekly targets.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "summer-challenge-2026",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "available",
    coachName: "James O'Brien",
    statusName: "Open",
    statusColor: PRIMARY,
    startedAt: null,
    nextReviewAt: null,
    progressPercent: 0,
    forms: [
      {
        id: "form_sc_join",
        name: "Join challenge",
        role: "first_submission",
        dueLabel: "Sign up",
        unlockDays: 0,
      },
    ],
  },
  {
    id: "tracker_programme_onboarding",
    name: "Programme Onboarding",
    description: "Get set up on your new programme with goals, schedule and preferences.",
    kind: TRACKER_KINDS.STANDARD,
    category: "journey",
    publicSlug: "programme-onboarding",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "available",
    coachName: "Sarah Murphy",
    statusName: "New",
    statusColor: "#b45309",
    startedAt: null,
    nextReviewAt: null,
    progressPercent: 0,
    forms: [
      {
        id: "form_po_main",
        name: "Onboarding form",
        role: "first_submission",
        dueLabel: "Start",
        unlockDays: 0,
      },
    ],
  },
  {
    id: "tracker_mobility_reset",
    name: "Mobility Reset",
    description: "A 21-day mobility focus for desk workers and stiff lifters.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "mobility-reset",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "available",
    coachName: "Aoife Walsh",
    statusName: "New",
    statusColor: "#b45309",
    startedAt: null,
    nextReviewAt: null,
    progressPercent: 0,
    forms: [
      {
        id: "form_mr_start",
        name: "Baseline screen",
        role: "first_submission",
        dueLabel: "Day 0",
        unlockDays: 0,
      },
      {
        id: "form_mr_end",
        name: "Day 21 review",
        role: "final",
        dueLabel: "Day 21",
        unlockDays: 21,
      },
    ],
  },
  {
    id: "tracker_hyrox_prep",
    name: "Hyrox Prep Track",
    description: "Race prep checkpoints for Hyrox — stations, conditioning and recovery.",
    kind: TRACKER_KINDS.JOURNEY,
    category: "journey",
    publicSlug: "hyrox-prep",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "available",
    coachName: "James O'Brien",
    statusName: "Open",
    statusColor: PRIMARY,
    startedAt: null,
    nextReviewAt: null,
    progressPercent: 0,
    forms: [
      {
        id: "form_hx_join",
        name: "Hyrox intake",
        role: "first_submission",
        dueLabel: "Sign up",
        unlockDays: 0,
      },
    ],
  },
  {
    id: "tracker_sleep_reset",
    name: "Sleep & Recovery Reset",
    description: "Two-week sleep and recovery habits with simple daily check-ins.",
    kind: TRACKER_KINDS.STANDARD,
    category: "journey",
    publicSlug: "sleep-recovery-reset",
    status: "live",
    trackerAccess: "gymrite_members",
    membership: "available",
    coachName: "Sarah Murphy",
    statusName: "New",
    statusColor: "#b45309",
    startedAt: null,
    nextReviewAt: null,
    progressPercent: 0,
    forms: [
      {
        id: "form_sr_main",
        name: "Sleep check-in",
        role: "first_submission",
        dueLabel: "Daily",
        unlockDays: 0,
      },
    ],
  },
];

const resolveMemberIdentity = (user) => {
  const id = String(user?._id || user?.id || "demo_member").trim();
  const email = String(user?.email || "member@gymrite.demo").trim().toLowerCase();
  const firstName =
    user?.first_name || user?.firstName || user?.name?.split?.(" ")?.[0] || "You";
  const lastName =
    user?.last_name || user?.lastName || user?.name?.split?.(" ")?.[1] || "";
  const memberName =
    `${firstName} ${lastName}`.trim() || user?.name || "Member";
  return { id, email, firstName, lastName, memberName };
};

const answerRows = (pairs) =>
  pairs.map(([label, value]) => ({ label, value }));

const buildMemberDemoSubmissions = (user) => {
  const me = resolveMemberIdentity(user);
  return [
    {
      id: "msub_cb_baseline",
      trackerId: "tracker_chasing_better",
      formId: "form_cb_signup",
      formName: "Sign-up & Baseline",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Submitted",
      statusColor: "#047857",
      coachName: "Sarah Murphy",
      answerSummary: "Goal: Build muscle · Starting weight: 78kg · Energy: 7/10",
      submittedAt: daysAgo(45),
      answers: answerRows([
        ["First Name", me.firstName],
        ["Last Name", me.lastName || "—"],
        ["Email Address", me.email],
        ["Phone Number", "+353 87 000 0000"],
        ["Preferred gym location", "City Centre"],
        ["Primary training focus for the next 30 days", "Build muscle"],
        [
          "Tell us about your fitness background",
          "Lifted for a few years, then took a break. Ready to get consistent again.",
        ],
        ["What is your main goal right now?", "Build muscle"],
        ["Which days can you usually train?", "Mon, Wed, Fri"],
        ["Starting weight (kg)", "78"],
        ["Target weight (kg)", "82"],
        ["Current energy level (1–10)", "7"],
      ]),
    },
    {
      id: "msub_cb_30",
      trackerId: "tracker_chasing_better",
      formId: "form_cb_30",
      formName: "30 Day Review",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Reviewed",
      statusColor: "#0369a1",
      coachName: "Sarah Murphy",
      answerSummary: "Weight: 76.2kg · Feeling good · On track",
      submittedAt: daysAgo(15),
      answers: answerRows([
        ["Current weight (kg)", "76.2"],
        ["How are you feeling overall?", "Good"],
        ["Progress toward your goal", "On track"],
        ["Energy level this week", "8"],
        ["Nutrition adherence", "Mostly on plan"],
        ["Wins this period", "Hit 3 sessions most weeks and sleep improved."],
        ["Anything else for your coach?", "Would like a deload week suggestion."],
      ]),
      coachNotes:
        "Nice consistency — keep protein high and we'll push upper body volume next block.",
    },
    {
      id: "msub_qr_q1",
      trackerId: "tracker_quarterly_review",
      formId: "form_qr_q1",
      formName: "Q1 Review",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Submitted",
      statusColor: "#047857",
      coachName: "James O'Brien",
      answerSummary: "Quarterly goal set · Attendance strong",
      submittedAt: daysAgo(80),
      answers: answerRows([
        ["Quarterly goal", "Drop body fat while keeping strength"],
        ["Attendance this quarter", "Strong — 3–4 sessions/week"],
        ["Biggest win", "More consistent morning sessions"],
        ["Biggest challenge", "Travel weeks and weekends"],
        ["Focus for next quarter", "Nutrition tracking + sleep"],
      ]),
    },
    {
      id: "msub_ci_1",
      trackerId: "tracker_coach_checkin",
      formId: "form_ci_main",
      formName: "Check-in form",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Submitted",
      statusColor: "#047857",
      coachName: "Aoife Walsh",
      answerSummary: "Energy 8 · Sessions 3 · No blockers",
      submittedAt: daysAgo(4),
      answers: answerRows([
        ["Energy this week (1–10)", "8"],
        ["Sessions completed", "3"],
        ["Any blockers?", "None"],
        ["How did training feel?", "Strong — recovered well"],
        ["Notes for coach", "Ready to add one more set on squats."],
      ]),
    },
    {
      id: "msub_ci_2",
      trackerId: "tracker_coach_checkin",
      formId: "form_ci_main",
      formName: "Check-in form",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Submitted",
      statusColor: "#047857",
      coachName: "Aoife Walsh",
      answerSummary: "Energy 6 · Sessions 2 · Sleep low",
      submittedAt: daysAgo(11),
      answers: answerRows([
        ["Energy this week (1–10)", "6"],
        ["Sessions completed", "2"],
        ["Any blockers?", "Sleep was low mid-week"],
        ["How did training feel?", "Okay — a bit flat on Thursday"],
        ["Notes for coach", "Travel on Wednesday — shorter session."],
      ]),
    },
    {
      id: "msub_ss_end",
      trackerId: "tracker_summer_done",
      formId: "form_ss_end",
      formName: "Final review",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Completed",
      statusColor: "#0369a1",
      coachName: "Sarah Murphy",
      answerSummary: "Strength up · Block finished",
      submittedAt: daysAgo(60),
      answers: answerRows([
        ["Final squat (kg)", "120"],
        ["How did the block feel?", "Strong finish"],
        ["Ready for next focus?", "Yes — hypertrophy"],
      ]),
    },
    {
      id: "msub_np_1",
      trackerId: "tracker_nutrition_pulse",
      formId: "form_np_main",
      formName: "Nutrition check-in",
      memberId: me.id,
      email: me.email,
      memberName: me.memberName,
      statusName: "Submitted",
      statusColor: "#047857",
      coachName: "Aoife Walsh",
      answerSummary: "Protein on target · 2 takeaways",
      submittedAt: daysAgo(3),
      answers: answerRows([
        ["Protein target hit?", "Yes"],
        ["Takeaway meals this week", "2"],
        ["Anything stuck?", "Evening snacks"],
      ]),
    },
  ];
};

const ensureSeeded = (user) => {
  const flag = localStorage.getItem(DEMO_FLAG_KEY);
  const trackers = readJson(TRACKERS_KEY, []);
  const submissions = readJson(SUBMISSIONS_KEY, []);
  if (flag === "1" && trackers.length && submissions.length) {
    return { trackers, submissions };
  }
  const nextTrackers = buildMemberDemoTrackers();
  const nextSubs = buildMemberDemoSubmissions(user);
  localStorage.setItem(TRACKERS_KEY, JSON.stringify(nextTrackers));
  localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(nextSubs));
  localStorage.setItem(DEMO_FLAG_KEY, "1");
  return { trackers: nextTrackers, submissions: nextSubs };
};

export const getMemberTrackers = (user) => {
  // Member portal list is API-backed (GET /api/tracker/member).
  // Keep local helpers for meta/grouping only — do not seed dummy trackers.
  void user;
  return [];
};

/** My = enrolled / working / completed. New = available, not started. */
export const isMyTracker = (tracker, user) => {
  if (!tracker) return false;
  if (tracker.membership === "available") return false;
  if (
    tracker.membership === "active" ||
    tracker.membership === "completed"
  ) {
    return true;
  }
  if ((tracker.submissionsCount || 0) > 0) return true;
  const subs = getMemberSubmissions(user, tracker.id);
  return subs.length > 0 || Boolean(tracker.startedAt);
};

export const groupMemberTrackers = (trackers = [], user) => {
  const all = (trackers || []).map((tracker) => ({
    tracker,
    meta: getTrackerListMeta(tracker, user),
  }));
  const my = all.filter(({ tracker }) => isMyTracker(tracker, user));
  const neu = all.filter(({ tracker }) => !isMyTracker(tracker, user));
  return { my, new: neu, all };
};

export const getMemberTrackersGrouped = (user) => {
  const all = getMemberTrackers(user).map((tracker) => ({
    tracker,
    meta: getTrackerListMeta(tracker, user),
  }));
  const my = all.filter(({ tracker }) => isMyTracker(tracker, user));
  const neu = all.filter(({ tracker }) => !isMyTracker(tracker, user));
  return { my, new: neu, all };
};

export const getMemberTrackersForSection = (user, section) => {
  const grouped = getMemberTrackersGrouped(user);
  if (section === TRACKER_LIST_SECTIONS.NEW) return grouped.new;
  if (section === TRACKER_LIST_SECTIONS.MY) return grouped.my;
  return grouped.all;
};

export const getMemberTrackerById = (id, user) =>
  getMemberTrackers(user).find((t) => t.id === id) || null;

export const getMemberSubmissions = (user, trackerId) => {
  const me = resolveMemberIdentity(user);
  const { submissions } = ensureSeeded(user);
  return submissions
    .filter((row) => {
      const mine =
        row.memberId === me.id ||
        String(row.email || "").toLowerCase() === me.email;
      if (!mine) return false;
      if (trackerId) return row.trackerId === trackerId;
      return true;
    })
    .sort(
      (a, b) =>
        new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    );
};

export const getMemberSubmissionById = (submissionId, user) => {
  const me = resolveMemberIdentity(user);
  const { submissions } = ensureSeeded(user);
  const row =
    submissions.find((item) => item.id === submissionId) || null;
  if (!row) return null;
  const mine =
    row.memberId === me.id ||
    String(row.email || "").toLowerCase() === me.email;
  return mine ? row : null;
};

/** Normalise answer list for display (array of {label,value} or object map). */
export const getSubmissionAnswerRows = (submission) => {
  if (!submission) return [];
  if (Array.isArray(submission.answers) && submission.answers.length) {
    return submission.answers
      .map((item) => {
        if (!item) return null;
        if (typeof item === "object" && (item.label || item.value != null)) {
          const value = Array.isArray(item.value)
            ? item.value.join(", ")
            : item.value;
          return {
            label: item.label || "Answer",
            value:
              value == null || value === ""
                ? "—"
                : String(value),
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (submission.answers && typeof submission.answers === "object") {
    return Object.entries(submission.answers).map(([label, value]) => ({
      label,
      value: Array.isArray(value)
        ? value.join(", ")
        : value == null || value === ""
          ? "—"
          : String(value),
    }));
  }
  return [];
};

/** Days until a form unlocks after journey start (baseline submit). */
export const resolveFormUnlockDays = (form, index = 0) => {
  if (form?.unlockDays != null && form.unlockDays !== "") {
    const n = Number(form.unlockDays);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  const nameMatch = String(form?.name || "").match(/(\d+)\s*[\-_ ]?\s*day/i);
  if (nameMatch) return Number(nameMatch[1]);
  if (
    index === 0 ||
    form?.role === "first_submission" ||
    form?.unlockType === "after_submit"
  ) {
    return 0;
  }
  return null;
};

export const getJourneyStartAt = (tracker, submissions = []) => {
  const sorted = [...(submissions || [])].sort(
    (a, b) =>
      new Date(a.submittedAt || 0).getTime() -
      new Date(b.submittedAt || 0).getTime()
  );
  return (
    sorted[0]?.submittedAt ||
    tracker?.startedAt ||
    tracker?.membership?.startedAt ||
    null
  );
};

const startOfLocalDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (value, days) => {
  const d = startOfLocalDay(value);
  if (!d) return null;
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

export const getJourneySteps = (tracker, submissions = []) => {
  const forms = Array.isArray(tracker?.forms) ? tracker.forms : [];
  const doneIds = new Set(submissions.map((s) => s.formId).filter(Boolean));
  const startAt = getJourneyStartAt(tracker, submissions);
  const today = startOfLocalDay(new Date());
  let foundNext = false;

  return forms.map((form, index) => {
    const done = doneIds.has(form.id);
    const unlockDays = resolveFormUnlockDays(form, index);
    const isBaseline =
      index === 0 ||
      form.role === "first_submission" ||
      unlockDays === 0;

    let unlockAt = null;
    let unlocked = true;
    if (!isBaseline && unlockDays != null) {
      if (!startAt) {
        unlocked = false;
      } else {
        unlockAt = addDays(startAt, unlockDays);
        unlocked = Boolean(today && unlockAt && today.getTime() >= unlockAt.getTime());
      }
    }

    let state = "upcoming";
    if (done) state = "done";
    else if (!foundNext) {
      state = unlocked ? "current" : "locked";
      foundNext = true;
    }

    const dueLabel =
      form.dueLabel ||
      (unlockDays != null && unlockDays > 0 ? `Day ${unlockDays}` : "") ||
      (isBaseline ? "Start" : "");

    return {
      ...form,
      index,
      state,
      unlocked,
      unlockDays,
      unlockAt: unlockAt ? unlockAt.toISOString() : null,
      dueLabel,
      submission:
        submissions.find((s) => s.formId === form.id) || null,
    };
  });
};

export const getTrackerListMeta = (tracker, user) => {
  const apiSubs = Array.isArray(tracker?._memberSubmissions)
    ? tracker._memberSubmissions
    : null;
  const subs =
    apiSubs ||
    (user ? getMemberSubmissions(user, tracker.id) : []);
  const steps = getJourneySteps(tracker, subs);
  const doneCount = steps.filter((s) => s.state === "done").length;
  const current = steps.find((s) => s.state === "current") || null;
  const locked = steps.find((s) => s.state === "locked") || null;
  const progress =
    steps.length > 0
      ? Math.round((doneCount / steps.length) * 100)
      : tracker.progressPercent || 0;
  const nextGate = current || locked || null;
  return {
    submissionsCount: subs.length || tracker.submissionsCount || 0,
    lastSubmittedAt:
      subs[0]?.submittedAt || tracker.lastSubmittedAt || null,
    doneCount,
    totalSteps: steps.length,
    currentStep: current,
    lockedStep: locked,
    canSubmit: Boolean(current),
    nextReviewAt:
      nextGate?.unlockAt ||
      tracker.nextReviewAt ||
      null,
    progress: progress || tracker.progressPercent || 0,
    isJourney:
      tracker.kind === TRACKER_KINDS.JOURNEY ||
      tracker.kind === "journey" ||
      steps.length > 1,
  };
};

export const reloadMemberTrackerDemos = (user) => {
  localStorage.removeItem(DEMO_FLAG_KEY);
  return ensureSeeded(user);
};

export { PRIMARY, resolveMemberIdentity };
